import React from "react";
import {
  FaCircle,
  FaCheck,
  FaExclamationTriangle,
  FaHourglassStart,
} from "react-icons/fa";

import { ExternalDmvStatus, TitleQuery } from "@Types/title_issues";
import { MutationError } from "@Types/index";

import { buildDmvStatusQuery } from "../titleIssueStatusQueries";
import styles from "./TitleIssueStatusCards.module.scss";

// Status configuration with icons, labels, and colors
const STATUS_CONFIG = {
  Untitled: {
    label: "Untitled",
    icon: FaHourglassStart,
    color: "#6b7280",
    activeColor: "#4f46e5",
  },
  "Not checked": {
    label: "Not Checked",
    icon: FaExclamationTriangle,
    color: "#ffbb6c",
    activeColor: "#d97706",
  },
  Titled: {
    label: "Titled",
    icon: FaCheck,
    color: "#10b981",
    activeColor: "#059669",
  },
} as const;

interface TitleIssueStatusCardsProps {
  titleQuery: TitleQuery;
  handleSetFilter: (type: "document" | "dmv", query: TitleQuery) => void;
  counts?: Record<ExternalDmvStatus, number>;
  isLoading: boolean;
  error: MutationError;
}

const TitleIssueStatusCards: React.FC<TitleIssueStatusCardsProps> = ({
  titleQuery,
  handleSetFilter,
  counts,
  isLoading,
  error,
}) => {
  const statusCounts = counts ?? {
    Untitled: 0,
    "Not checked": 0,
    Titled: 0,
  };

  const handleStatusClick = (status: ExternalDmvStatus) => {
    if (status === titleQuery?.active) {
      // Deactivate current filter
      handleSetFilter("dmv", undefined);
    } else {
      // Set new filter
      handleSetFilter("dmv", {
        active: status,
        query: buildDmvStatusQuery(status),
      } as TitleQuery);
    }
  };

  const handleKeyDown = (
    event: React.KeyboardEvent,
    status: ExternalDmvStatus
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleStatusClick(status);
    }
  };

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorMessage}>
          Failed to load status information. Please try again.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.statusCardsContainer}>
      <h3 className={styles.sectionLabel}>DMV Status Filter</h3>

      <div className={styles.statusCardsGrid}>
        {(Object.keys(STATUS_CONFIG) as ExternalDmvStatus[]).map((status) => {
          const config = STATUS_CONFIG[status];
          const count = statusCounts[status];
          const isActive = titleQuery?.active === status;
          const IconComponent = config.icon;

          return (
            <div
              key={status}
              className={`${styles.statusCard} ${
                styles[status === "Not checked" ? "NotChecked" : status]
              } ${isActive ? styles.active : ""} ${
                isLoading ? styles.loading : ""
              }`}
              onClick={() => !isLoading && handleStatusClick(status)}
              onKeyDown={(e) => !isLoading && handleKeyDown(e, status)}
              role="button"
              tabIndex={0}
              aria-pressed={isActive}
              aria-label={`Filter by ${config.label} status, ${count} items`}
              aria-disabled={isLoading}
            >
              <div className={styles.statusCardContent}>
                <div className={styles.statusInfo}>
                  <div className={styles.statusIcon}>
                    <IconComponent
                      size={20}
                      color={isActive ? config.activeColor : config.color}
                      aria-hidden="true"
                    />
                  </div>
                  <div className={styles.statusText}>
                    <span className={styles.statusLabel}>{config.label}</span>
                    <span
                      className={styles.statusCount}
                      aria-label={`${count} items`}
                    >
                      ({count})
                    </span>
                  </div>
                </div>

                {isActive && (
                  <div className={styles.activeIndicator}>
                    <FaCircle
                      size={8}
                      color={config.activeColor}
                      aria-hidden="true"
                    />
                  </div>
                )}
              </div>

              {isLoading && (
                <div className={styles.loadingOverlay}>
                  <div className={styles.spinner} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TitleIssueStatusCards;
